<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { FIELD_CLASS } from '../constants';
import { reportInvalidForm } from '../formFeedback';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

export default {
  name: 'AgentModal',
  props: {
    agent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      form: {
        name: this.agent?.name || '',
        email: this.agent?.email || '',
        phone: this.agent?.phone || '',
      },
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.agent;
    },
    fieldErrors() {
      return {
        name: !this.form.name.trim(),
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  methods: {
    ...mapActions('realEstateAgents', ['addAgent', 'updateAgent']),
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        if (this.isEdit) {
          await this.updateAgent({
            agentId: this.agent.agent_id,
            fields: { ...this.form },
          });
          useAlert(this.$t('REAL_ESTATE.AGENTS.SUCCESS_UPDATED'));
        } else {
          await this.addAgent({ ...this.form });
          useAlert(this.$t('REAL_ESTATE.AGENTS.SUCCESS_ADDED'));
        }
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(
            this,
            err,
            this.isEdit
              ? 'REAL_ESTATE.AGENTS.ERROR_UPDATE'
              : 'REAL_ESTATE.AGENTS.ERROR_ADD'
          )
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{
            isEdit
              ? $t('REAL_ESTATE.AGENTS.MODAL_EDIT_TITLE')
              : $t('REAL_ESTATE.AGENTS.MODAL_ADD_TITLE')
          }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.FIELD_NAME') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            :placeholder="$t('REAL_ESTATE.AGENTS.FIELD_NAME_PLACEHOLDER')"
            type="text"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.name,
              },
            ]"
            required
          />
          <p
            v-if="submitted && fieldErrors.name"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.FIELD_EMAIL') }}
          </label>
          <input
            v-model="form.email"
            :placeholder="$t('REAL_ESTATE.AGENTS.FIELD_EMAIL_PLACEHOLDER')"
            type="email"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.AGENTS.FIELD_PHONE') }}
          </label>
          <input
            v-model="form.phone"
            :placeholder="$t('REAL_ESTATE.AGENTS.FIELD_PHONE_PLACEHOLDER')"
            type="text"
            :class="fieldClass"
          />
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.AGENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.AGENTS.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
